import { createFileRoute } from "@tanstack/react-router";
import { requireAdminRoute } from "@/lib/require-admin-route";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import { useTopbar } from "@/components/chrome/use-topbar";

interface IcpRules {
  priorLoansMin: number;
  loanBandRespected: boolean;
  requireContact: boolean;
  geoMatchRequired: boolean;
  blacklistEnforced: boolean;
  negativeRemarksBlock: boolean;
}

const DEFAULTS: IcpRules = {
  priorLoansMin: 1,
  loanBandRespected: true,
  requireContact: false,
  geoMatchRequired: true,
  blacklistEnforced: true,
  negativeRemarksBlock: true,
};

interface BoolRow {
  key: Exclude<keyof IcpRules, "priorLoansMin">;
  label: string;
  description: string;
}

const BOOL_ROWS: BoolRow[] = [
  {
    key: "loanBandRespected",
    label: "Respect market loan band",
    description: "Loan size must sit inside the market's configured loan band.",
  },
  {
    key: "requireContact",
    label: "Require borrower contact info",
    description: "Skip borrowers without a phone or email on file.",
  },
  {
    key: "geoMatchRequired",
    label: "Geo match required",
    description: "Borrower's prior activity must overlap one of our active markets.",
  },
  {
    key: "blacklistEnforced",
    label: "Enforce blacklists",
    description: "Drop borrowers and lenders that appear on a blacklist.",
  },
  {
    key: "negativeRemarksBlock",
    label: "Block on negative remarks",
    description: "Skip borrowers with negative remarks on recent loans.",
  },
];

export const Route = createFileRoute("/_app/settings/icp")({
  beforeLoad: requireAdminRoute,
  component: IcpPage,
});

function IcpPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery<IcpRules | null>({
    queryKey: ["settings", "icp"],
    queryFn: () => apiFetch<IcpRules | null>("/api/settings/icp"),
  });

  const [form, setForm] = useState<IcpRules>(DEFAULTS);
  const [seeded, setSeeded] = useState(false);

  const { setTopbar } = useTopbar();
  useEffect(() => {
    setTopbar({
      crumbs: [
        { label: "Operations" },
        { label: "Settings", to: "/settings" },
        { label: "ICP rules" },
      ],
      rightSlot: null,
    });
    return () => setTopbar({ crumbs: [], rightSlot: null });
  }, [setTopbar]);

  useEffect(() => {
    if (seeded) return;
    if (isLoading) return;
    setForm(data ?? DEFAULTS);
    setSeeded(true);
  }, [data, isLoading, seeded]);

  const save = useMutation({
    mutationFn: (payload: IcpRules) =>
      apiFetch<{ ok: true }>("/api/settings/icp", {
        method: "PUT",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings", "icp"] }),
  });

  if (isLoading) return <p>Loading…</p>;
  if (error) return <p className="text-red-400">Failed to load ICP rules.</p>;

  return (
    <div className="space-y-6">
      <div className="border-b border-rule pb-5">
        <h1 className="mb-1 text-3xl font-semibold tracking-tight text-ink">ICP rules</h1>
        <p className="text-sm text-muted">
          Edit the pass/fail rules that decide whether a matched borrower is worth mailing.
        </p>
      </div>

      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(form);
        }}
      >
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-4 rounded border border-rule-strong bg-surface px-4 py-3">
          <div>
            <div className="text-sm font-medium">Minimum prior loans</div>
            <div className="text-xs text-muted">
              Borrower must have closed at least this many qualifying loans.
            </div>
          </div>
          <input
            type="number"
            min={0}
            max={10}
            value={form.priorLoansMin}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                priorLoansMin: Math.max(0, Math.min(10, Number(e.target.value))),
              }))
            }
            className="w-full md:w-20 rounded border border-rule-strong bg-surface px-3 py-1.5 text-right text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold"
          />
        </div>

        {BOOL_ROWS.map((row) => {
          const active = form[row.key];
          return (
            <div
              key={row.key}
              className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-4 rounded border border-rule-strong bg-surface px-4 py-3"
            >
              <div>
                <div className="text-sm font-medium">{row.label}</div>
                <div className="text-xs text-muted">{row.description}</div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={active}
                onClick={() => setForm((f) => ({ ...f, [row.key]: !active }))}
                className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                  active ? "bg-gold" : "bg-paper-3"
                }`}
              >
                <span
                  className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-ink transition-transform ${
                    active ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
          );
        })}

        <div className="flex items-center justify-end pt-2">
          <button
            type="submit"
            disabled={save.isPending}
            className="rounded bg-gold px-4 py-2 text-sm font-semibold text-ink disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
